import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

import { Input, Modal } from '@unified-checkout/design-system';

import type { IField } from '@app/dashboard/types/outerLayout/common';
import type {
  TSaveChangeApiPayload,
  IToggleConfirmationModalProps,
} from '@app/dashboard/types/innerLayout/notifications/modals/toggle-confirmation';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { useLangStore } from '@app/dashboard/store/lang';
import {
  ADD_PASSWORD_FIELD_INITIAL_STATE,
  FIELDS,
  NOTIFICATION_FLAGS,
} from '@app/dashboard/constants/settings/notifications';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { onFieldChangeHandler } from '@app/dashboard/utils/formValidations';
import { useMerchantFlags } from '@app/dashboard/hooks/useMerchantFlags';
import { notify } from '@app/dashboard/utils/notifier';

import styles from './ToggleModalConfirmation.module.scss';

const ToggleConfirmation = ({
  isModalOpen,
  setIsModalOpen,
  updatedFlags,
}: IToggleConfirmationModalProps) => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();
  const { flagMutation: saveChangesMutation } = useMerchantFlags();

  const [fields, setFields] = useState<IField>(
    ADD_PASSWORD_FIELD_INITIAL_STATE(t)
  );

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const targetValue = e.target.value;
    onFieldChangeHandler(
      fields,
      setFields,
      FIELDS.PASSWORD,
      targetValue,
      () => {}
    );
  };

  const handleSuccess = async () => {
    notify.success(t('innerLayout.notificationsPage.toaster.saveChanges'))
    setFields(ADD_PASSWORD_FIELD_INITIAL_STATE(t));
    setIsModalOpen(false);
  };

  const handleError = (axiosErr: AxiosError<{ message?: string }>) => {
    let message = t('errors.somethingWentWrong');

    if (axiosErr.response?.data) {
      const data = axiosErr.response?.data as { message?: string } | any;
      if (data?.message) {
        message = data.message;
      } else if (typeof data === 'object') {
        message = data[lang];
      }
    } else if (axiosErr.message) {
      message = axiosErr.message;
    }

    notify.error(message);
  };

  const handleClick = () => {
    if (!updatedFlags) return;
    let payload = { ...updatedFlags };

    const skipCustomerNotificationKey =
      NOTIFICATION_FLAGS.SKIP_CUSTOMER_TRANSACTION_EMAIL as keyof typeof updatedFlags;

    const isContainedSkipCustomerNotificationKey = Object.keys(
      updatedFlags
    ).includes(skipCustomerNotificationKey);

    if (isContainedSkipCustomerNotificationKey) {
      payload = {
        ...updatedFlags,
        [skipCustomerNotificationKey]:
          !updatedFlags[skipCustomerNotificationKey],
      };
    }
    const finalPayload: TSaveChangeApiPayload = {
      ...payload,
      password: fields[FIELDS.PASSWORD].value as string,
    };

    saveChangesMutation.mutate(finalPayload, {
      onSuccess: handleSuccess,
      onError: (error: Error | AxiosError<{ message?: string }>) => {
        handleError(error as AxiosError<{ message?: string }>);
      },
    });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    //restting the password field when opening the modal
    if (isModalOpen) {
      setFields(ADD_PASSWORD_FIELD_INITIAL_STATE(t));
    }
  }, [isModalOpen]);

  return (
    <Modal
      open={isModalOpen}
      isOverlayAboveModal
      isMobile={isMobile}
      title={t('innerLayout.notificationsPage.modals.password.title')}
      btnsProps={[
        {
          size: 'md',
          color: 'primary',
          variant: 'contained',
          text: t('innerLayout.notificationsPage.modals.actions.confirm'),
          onClickHandler: handleClick,
          disabled:
            !fields[FIELDS.PASSWORD].isValid || saveChangesMutation.isPending,
          loading: saveChangesMutation.isPending,
        },
        {
          size: 'md',
          color: 'secondary',
          variant: 'outlined',
          text: t('innerLayout.notificationsPage.modals.actions.cancel'),
          onClickHandler: handleCancel,
          disabled: saveChangesMutation.isPending,
        },
      ]}
    >
      <div className={styles['toggle-confirmation-wrapper']}>
        <Input
          name={FIELDS.PASSWORD}
          type="password"
          label={t('innerLayout.notificationsPage.modals.labels.password')}
          placeholder={t(
            'innerLayout.notificationsPage.modals.placeholders.password'
          )}
          onChange={handleFieldChange}
          value={fields[FIELDS.PASSWORD].value}
          error={fields[FIELDS.PASSWORD].errorMsg}
          status={fields[FIELDS.PASSWORD].errorMsg ? 'error' : 'default'}
        />
      </div>
    </Modal>
  );
};

export default ToggleConfirmation;